// C.1 — способности нейтральных юнитов.
//
// Карты с реализованными способностями:
//   - Уличный шаман (neutral_ulichnyy_shaman) — Order: 1 урон enemy_choose
//   - Старик с ИЭД (neutral_starik_s_ied) — Deathrattle: 2 урона enemy_aoe_line
//   - Контрабандист дронов (neutral_kontrabandist_dronov) — Order: призыв
//                          neutral_drone_mukha 1/0/1 на свободную ячейку
//
// Без способностей (только базовые статы/keyword'ы из xlsx):
//   - «Чумба» с трубой 2/0/3
//   - Сборщик мусора 1/1/4 [taunt]
//   - Тень с ножом 3/0/2 [stealth]
//   - Бугай со шкворнем 3/0/3 [pierce]
//   - Дрон-«муха» 1/0/1 (призывной токен)

import { registerImplementedAbility } from '../warn-unimplemented.js';
import { registerC1Order } from '../global/c1/order-registry.js';
import { registerOnUnitDiedExtraHandler, onUnitSummoned } from '../deus/hooks.js';
import { applyDamage } from '../../combat.js';
import { summonUnit } from '../global/summon.js';

// === Уличный шаман ===
export const SHAMAN_ID = 'neutral_ulichnyy_shaman';

export function shamanOrder(state, sourceCard, opts = {}) {
  const target = opts.target;
  if (!target) return { ok: false, reason: 'no_target' };
  if (target.type !== 'unit') return { ok: false, reason: 'not_unit' };
  if (target.ownerId === sourceCard.ownerId) return { ok: false, reason: 'not_enemy' };
  if (!target.isAlive?.()) return { ok: false, reason: 'target_dead' };

  applyDamage(target, 1, { state, source: sourceCard });
  state.log?.add(`🔮 ${sourceCard.name_ru}: 1 урон ${target.name_ru}`);
  return { ok: true };
}
registerImplementedAbility(SHAMAN_ID, 'order');
registerC1Order(SHAMAN_ID, shamanOrder);

// === Старик с ИЭД (Посмертие) ===
export const STARIK_ID = 'neutral_starik_s_ied';
const STARIK_SRC = { id: STARIK_ID, name_ru: 'Старик с ИЭД', type: 'unit' };

registerOnUnitDiedExtraHandler((state, payload) => {
  if (!state || !payload) return;
  const dead = payload.card;
  if (!dead || dead.id !== STARIK_ID) return;
  if (dead.type !== 'unit') return;
  // Молчание на момент смерти отменяет Посмертие.
  if (dead.current?.silenced) return;
  if (dead.silenceSources && dead.silenceSources.size > 0) return;

  const ownerIdx = payload.ownerIdx;
  // Этап 56-fix: к моменту onUnitDied смерть уже вызвала removeFromField,
  // который обнулил dead.line (сохранив lastLine). Берём line с фоллбэком —
  // иначе Посмертие читало null и не срабатывало.
  const line = dead.line ?? dead.lastLine;
  if (ownerIdx == null || line == null) return;

  // Враги на той же линии (line 0 или 1 на стороне противника).
  const enemyIdx = 1 - ownerIdx;
  const enemy = state.players?.[enemyIdx];
  if (!enemy?.field) return;
  const row = enemy.field[line];
  if (!row) return;

  let hit = 0;
  for (const u of row) {
    if (!u || u.type !== 'unit') continue;
    if (!u.isAlive?.()) continue;
    applyDamage(u, 2, { state, source: STARIK_SRC });
    hit++;
  }
  if (hit > 0) {
    state.log?.add(`💥 ${STARIK_SRC.name_ru} (Посмертие): 2 урона ${hit} врагам на линии ${line + 1}`);
  }
});
registerImplementedAbility(STARIK_ID, 'deathrattle');

// === Контрабандист дронов ===
export const KONTRABANDIST_ID = 'neutral_kontrabandist_dronov';
export const DRONE_ID = 'neutral_drone_mukha';

// Ищет первую свободную ячейку у игрока (1-я линия, потом 2-я).
function findFirstFreeSlot(player) {
  if (!player?.field) return null;
  for (let line = 0; line < 2; line++) {
    const row = player.field[line];
    if (!row) continue;
    for (let slot = 0; slot < row.length; slot++) {
      if (row[slot] === null) return { line, slot };
    }
  }
  return null;
}

export function kontrabandistOrder(state, sourceCard) {
  const ownerIdx = sourceCard.ownerId;
  if (ownerIdx == null) return { ok: false, reason: 'no_owner' };
  const owner = state.players?.[ownerIdx];
  if (!owner) return { ok: false, reason: 'no_owner_state' };

  // Аудит-фикс под v6: «на случайную ячейку» (а не первую свободную).
  const free = [];
  for (let line = 0; line < 2; line++) {
    const row = owner.field?.[line];
    if (!row) continue;
    for (let s = 0; s < row.length; s++) if (row[s] === null) free.push({ line, slot: s });
  }
  if (free.length === 0) return { ok: false, reason: 'no_free_slot' };
  const slot = free[state.rng?.int ? state.rng.int(free.length) : Math.floor(Math.random() * free.length)];

  const drone = summonUnit(state, ownerIdx, DRONE_ID, slot.line, slot.slot);
  if (!drone) return { ok: false, reason: 'summon_failed' };

  // Дёрнуть общую шину призыва (для пересчёта аур, line-triggers и т.п.).
  try { onUnitSummoned(state, ownerIdx, drone); } catch {}

  state.log?.add(`🛸 ${sourceCard.name_ru}: призван Дрон-«муха» на линии ${slot.line + 1}`);
  return { ok: true, summoned: drone };
}
registerImplementedAbility(KONTRABANDIST_ID, 'order');
registerC1Order(KONTRABANDIST_ID, kontrabandistOrder);
